import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TranslationDto } from '@/app/services/api/translationService';

/**
 * Redux state for entity-level translations fetched from the DB.
 * Cache is keyed by locale string (e.g. "zh-TW").
 */
interface TranslationState {
    cache: Record<string, TranslationDto[]>;
    loadedLocales: string[];
    isLoading: boolean;
    error: string | null;
}

const initialState: TranslationState = {
    cache: {},
    loadedLocales: [],
    isLoading: false,
    error: null,
};

const translationSlice = createSlice({
    name: 'translation',
    initialState,
    reducers: {
        setTranslations: (
            state,
            action: PayloadAction<{ locale: string; translations: TranslationDto[] }>
        ) => {
            const { locale, translations } = action.payload;
            state.cache[locale] = translations;
            if (!state.loadedLocales.includes(locale)) {
                state.loadedLocales.push(locale);
            }
        },
        clearCache: (state) => {
            state.cache = {};
            state.loadedLocales = [];
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
    },
});

export const {
    setTranslations,
    clearCache,
    setLoading,
    setError,
} = translationSlice.actions;

export default translationSlice.reducer;

// ── Selectors ────────────────────────────────────────────────────────────────

/** Returns the cached translations array for a given locale. */
export const selectTranslationsByLocale = (locale: string) =>
    (state: { translation: TranslationState }) =>
        state.translation.cache[locale] ?? [];

/** Returns true if translations for the given locale have already been fetched. */
export const selectIsLocaleLoaded = (locale: string) =>
    (state: { translation: TranslationState }) =>
        state.translation.loadedLocales.includes(locale);

/**
 * Finds a specific translation value from the cache.
 * Returns undefined if not found.
 */
export const selectTranslationValue = (
    locale: string,
    entityType: string,
    entityId: number,
    fieldName: string
) =>
    (state: { translation: TranslationState }): string | undefined => {
        const translations = state.translation.cache[locale];
        if (!translations) return undefined;
        const match = translations.find(
            (t) =>
                t.entityType === entityType &&
                t.entityId === entityId &&
                t.fieldName === fieldName
        );
        return match?.value;
    };

export type { TranslationState };
